<template>
  <div class="ad-setting-container">
    <div class="ad-setting-content">
      <div class="ad-setting-header">
        <h2>广告设置</h2>
        <p>配置广告显示规则和奖励策略</p>
      </div>

      <el-divider content-position="left">
        <i class="el-icon-star-on"></i> 个人中心 - 广告任务奖励
      </el-divider>

      <el-form :model="form" label-width="140px" class="ad-form">
        <el-form-item label="启用广告奖励">
          <el-switch v-model="form.enabled" active-text="开启" inactive-text="关闭"></el-switch>
          <div class="help-text">开启后，免费版用户在看广告后可获得额外的生成次数</div>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="广告时长（秒）">
              <el-input-number v-model="form.adDuration" :min="3" :max="60" :step="1" controls-position="right"></el-input-number>
              <div class="help-text">用户需要等待的倒计时时长</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="每天最多观看次数">
              <el-input-number v-model="form.dailyMaxAds" :min="1" :max="20" :step="1" controls-position="right"></el-input-number>
              <div class="help-text">每个用户每天最多可观看的广告次数</div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="每次奖励次数">
          <el-tag type="success" size="medium">每次观看 +1 次生成机会</el-tag>
          <div class="help-text">用户每看完一次广告，获得 1 次额外生成次数</div>
        </el-form-item>
      </el-form>

      <el-divider content-position="left">
        <i class="el-icon-menu"></i> 全站广告投放（预留）
      </el-divider>

      <el-form :model="form" label-width="140px" class="ad-form">
        <el-form-item label="广告类型">
          <el-radio-group v-model="form.adType">
            <el-radio label="rewarded">激励视频广告</el-radio>
            <el-radio label="banner">横幅广告</el-radio>
            <el-radio label="interstitial">插屏广告</el-radio>
            <el-radio label="native">原生广告</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="广告位">
          <el-checkbox-group v-model="form.adPositions">
            <el-checkbox label="profile-rewarded">个人中心-任务奖励</el-checkbox>
            <el-checkbox label="image-generate">图片生成页面</el-checkbox>
            <el-checkbox label="video-generate">视频生成页面</el-checkbox>
            <el-checkbox label="drama-generate">短剧生成页面</el-checkbox>
            <el-checkbox label="text-generate">文案创作页面</el-checkbox>
          </el-checkbox-group>
          <div class="help-text">选择广告投放的页面位置（未来扩展用）</div>
        </el-form-item>

        <el-form-item label="显示规则">
          <el-select v-model="form.displayRule" placeholder="选择显示规则" style="width: 100%">
            <el-option label="每日任务奖励" value="daily-rewarded"></el-option>
            <el-option label="每次生成前显示" value="every-time"></el-option>
            <el-option label="每天显示一次" value="once-per-day"></el-option>
            <el-option label="每小时显示一次" value="once-per-hour"></el-option>
          </el-select>
          <div class="help-text">控制广告在选定位置的出现频率</div>
        </el-form-item>

        <el-form-item label="广告代码">
          <el-input
            v-model="form.adCode"
            type="textarea"
            :rows="4"
            placeholder="请输入第三方广告平台提供的广告代码（如 Google AdSense、穿山甲等）"
          ></el-input>
          <div class="help-text">用于接入第三方广告平台的 SDK 代码</div>
        </el-form-item>

        <el-form-item label="会员豁免">
          <el-switch v-model="form.memberExemption" active-text="开启" inactive-text="关闭"></el-switch>
          <div class="help-text">开启后，付费会员用户不受广告限制</div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="saveSettings" :loading="loading">保存设置</el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>

      <el-divider content-position="left">
        <i class="el-icon-document"></i> 使用说明
      </el-divider>

      <div class="usage-guide">
        <el-collapse>
          <el-collapse-item title="如何配置个人中心广告任务？">
            <p>1. 开启"启用广告奖励"开关</p>
            <p>2. 设置广告时长（建议 5-15 秒）</p>
            <p>3. 设置每天最多观看次数（建议 3-5 次）</p>
            <p>4. 保存设置后，免费版用户在个人中心即可看到广告任务</p>
          </el-collapse-item>
          <el-collapse-item title="如何接入第三方广告？">
            <p>1. 在广告平台（如 Google AdSense、穿山甲等）注册账号</p>
            <p>2. 获取广告代码并粘贴到"广告代码"输入框</p>
            <p>3. 选择合适的广告类型和投放位置</p>
            <p>4. 注意：当前广告代码功能主要用于未来全站广告投放扩展</p>
          </el-collapse-item>
          <el-collapse-item title="广告奖励规则说明">
            <p>• 每次观看广告：用户获得 +1 次生成机会</p>
            <p>• 每日上限：达到"每天最多观看次数"后，当天无法再看广告</p>
            <p>• 次日重置：每日 00:00 自动重置广告次数和生成次数</p>
            <p>• 会员豁免：开启后，付费会员不受广告次数限制</p>
          </el-collapse-item>
        </el-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdSetting',
  data() {
    return {
      form: {
        enabled: false,
        adType: 'rewarded',
        adPositions: ['profile-rewarded'],
        adCode: '',
        displayRule: 'daily-rewarded',
        adDuration: 5,
        dailyMaxAds: 3,
        maxBonusPerDay: 3,
        memberExemption: true
      },
      loading: false
    }
  },
  mounted() {
    this.initSettings()
  },
  methods: {
    initSettings() {
      axios.get('/admin/ad-setting')
        .then(response => {
          const data = response.data
          this.form = {
            enabled: data.enabled !== undefined ? data.enabled : false,
            adType: data.adType || 'rewarded',
            adPositions: data.adPositions || ['profile-rewarded'],
            adCode: data.adCode || '',
            displayRule: data.displayRule || 'daily-rewarded',
            adDuration: data.adDuration || 5,
            dailyMaxAds: data.dailyMaxAds || 3,
            maxBonusPerDay: data.maxBonusPerDay || 3,
            memberExemption: data.memberExemption !== undefined ? data.memberExemption : true
          }
        })
        .catch(() => {
          this.$message.error('获取设置失败，请稍后重试')
        })
    },
    saveSettings() {
      this.loading = true
      axios.put('/admin/ad-setting', this.form)
        .then(() => {
          this.$message.success('设置保存成功')
        })
        .catch(() => {
          this.$message.error('保存设置失败，请稍后重试')
        })
        .finally(() => {
          this.loading = false
        })
    },
    resetForm() {
      this.initSettings()
      this.$message.info('表单已重置')
    }
  }
}
</script>

<style scoped>
.ad-setting-container {
  padding: 20px 0;
}

.ad-setting-content {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #333;
}

.ad-setting-header {
  margin-bottom: 30px;
}

.ad-setting-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.ad-setting-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.help-text {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.5;
}

.ad-form {
  margin-bottom: 10px;
}

.el-form-item__label {
  color: #333;
  font-weight: 500;
}

.el-button--primary {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  border: none;
  color: #000;
  font-weight: 600;
}

.el-button--primary:hover {
  background: linear-gradient(135deg, #00cc99, #00b386);
  color: #000;
}

.el-divider__text {
  color: #333;
  font-weight: 600;
  font-size: 14px;
}

.el-divider__text i {
  margin-right: 6px;
  color: #00e6ac;
}

.usage-guide {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
}

.usage-guide p {
  margin: 8px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}

.el-collapse {
  border: none;
}

.el-collapse-item__header {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  background: #fff;
  border-bottom: 1px solid #eee;
}

.el-collapse-item__content {
  padding: 15px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.8;
}

.el-tag {
  border: none;
}
</style>
